import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { coins, transactions, clients, certLookup, images, tags, getAuthHeader, API_BASE_URL } from '../../utils/api';
import TagSelector from '../TagSelector';
import { fetchCertImages } from '../../utils/certImages';
import SaleTransactionModal from '../SaleTransactionModal';
import FieldMapping from './FieldMapping';
import DataPreview from './DataPreview';
import ImportLog from './ImportLog';
import { requiredFields, optionalFields, salesFields } from './constants';
import { attemptAutoMapping, isEmpty } from './utils';
import { validateRow, validateTransactionData } from './validation';

const BulkImportForm = ({ onComplete }) => {
  // File state
  const [file, setFile] = useState(null);
  const [salesFile, setSalesFile] = useState(null);
  const [useSalesSpreadsheet, setUseSalesSpreadsheet] = useState(false);

  // Data state
  const [headers, setHeaders] = useState([]);
  const [data, setData] = useState([]);
  const [mappings, setMappings] = useState({});
  const [salesHeaders, setSalesHeaders] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [salesMappings, setSalesMappings] = useState({});

  // Import state
  const [importing, setImporting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);

  // PCGS state
  const [updateFromPCGS, setUpdateFromPCGS] = useState(true);
  const [fetchImages, setFetchImages] = useState(true);
  const [pcgsUpdates, setPcgsUpdates] = useState({});
  const [revertedUpdates, setRevertedUpdates] = useState(new Set());

  // Transaction state
  const [validatedTransactions, setValidatedTransactions] = useState([]);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [currentSoldCoin, setCurrentSoldCoin] = useState(null);
  const [pendingSoldCoins, setPendingSoldCoins] = useState([]);
  const [soldCoinTransactions, setSoldCoinTransactions] = useState({});

  // Combine required and optional fields
  const availableFields = [...requiredFields, ...optionalFields];

  const addLog = (message, type = 'info', phase = 'general', details = null) => {
    setLogs(prev => [...prev, { 
      message, 
      type, 
      phase,
      details,
      timestamp: new Date().toISOString(),
    }]);
  };

  // Process certification lookups in batches
  const processCertBatch = useCallback(async (certNumbers, options = {}) => {
    const { batchSize = 5 } = options;
    const results = {};
    let processed = 0;

      addLog(
        `Starting certification lookup for ${certNumbers.length} coins`,
        'info',
        'cert-lookup',
        `Processing in batches of ${batchSize}`
      );

      // Process in smaller batches
      for (let i = 0; i < certNumbers.length; i += batchSize) {
        const batch = certNumbers.slice(i, i + batchSize);
        const batchNumber = Math.floor(i / batchSize) + 1;
        const totalBatches = Math.ceil(certNumbers.length / batchSize);
        
        addLog(
          `Processing batch ${batchNumber} of ${totalBatches}`,
          'info',
          'cert-lookup',
          `Batch size: ${batch.length} coins`
        );

        // Process batch concurrently
        const batchPromises = batch.map(async (certNumber) => {
        try {
          // Determine service based on cert number format
          // Determine service based on cert number format
          const service = /^\d+$/.test(certNumber) ? 'pcgs' : 
                         /^\d{7}-\d{3}$/.test(certNumber) ? 'ngc' : null;
          
          if (!service) {
            addLog(
              `Invalid certification number format: ${certNumber}`,
              'error',
              'cert-lookup',
              'Number format does not match PCGS or NGC pattern'
            );
            return;
          }

          addLog(
            `Looking up ${service.toUpperCase()} certification #${certNumber}`,
            'info',
            'cert-lookup',
            'Fetching data from certification service...'
          );

          const data = await certLookup.lookup(service, certNumber);
          if (data) {
            // Add the certification service to the data
            results[certNumber] = {
              ...data,
              certification_service_id: service === 'pcgs' ? 1 : service === 'ngc' ? 2 : null
            };
            addLog(
              `Successfully retrieved ${service.toUpperCase()} data for #${certNumber}`,
              'success',
              'cert-lookup',
              `Status: Found\nService: ${service.toUpperCase()}\nUpdated fields: ${Object.keys(data).join(', ')}`
            );
          }
        } catch (error) {
          addLog(
            `Failed to fetch data for #${certNumber}`,
            'error',
            'cert-lookup',
            `Status: Failed\nService: ${service.toUpperCase()}\nError: ${error.message}`
          );
        }
        processed++;
      });

      // Wait for batch to complete before processing next batch
      await Promise.all(batchPromises);
      
      // Small delay between batches to avoid rate limits
      if (i + batchSize < certNumbers.length) {
        addLog(
          'Rate limit delay',
          'info',
          'cert-lookup',
          'Waiting 1 second before next batch to avoid rate limits'
        );
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    return results;
  }, [addLog]);

  const handleFileUpload = (e, isSalesFile = false) => {
    const uploadedFile = e.target.files[0];
    if (isSalesFile) {
      setSalesFile(uploadedFile);
    } else {
      setFile(uploadedFile);
    }
    
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const isCSV = uploadedFile.name.toLowerCase().endsWith('.csv');
        const workbook = isCSV 
          ? XLSX.read(evt.target.result, { type: 'string' })
          : XLSX.read(evt.target.result, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          setError('File must contain headers and at least one data row');
          return;
        }

        const headers = jsonData[0];
        // Filter out empty rows (rows where all cells are empty)
        const data = jsonData.slice(1).filter(row => 
          row.some(cell => cell !== undefined && cell !== null && cell !== '')
        );

        // Log file details
        const fileType = isSalesFile ? 'Sales' : 'Coin';
        addLog(
          `${fileType} file "${uploadedFile.name}" loaded successfully`,
          'success',
          'file-processing',
          `Found ${headers.length} columns and ${data.length} data rows`
        );

        // Attempt auto-mapping and log results
        const mappingFields = isSalesFile ? salesFields : availableFields;
        const autoMappings = attemptAutoMapping(headers, mappingFields);
        const mappedFields = Object.values(autoMappings).filter(Boolean);
        
        addLog(
          `Auto-mapped ${mappedFields.length} out of ${headers.length} columns`,
          mappedFields.length > 0 ? 'success' : 'warn',
          'file-processing',
          `Mapped fields: ${mappedFields.join(', ')}`
        );

        if (isSalesFile) {
          setSalesHeaders(headers);
          setSalesData(data);
          setSalesMappings(autoMappings);
        } else {
          setHeaders(headers);
          setData(data);
          setMappings(autoMappings);
        }
        
        // Clear file reference and input value
        if (isSalesFile) {
          setSalesFile(null);
        } else {
          setFile(null);
        }
        e.target.value = '';
      } catch (err) {
        console.error('File parsing error:', err);
        setError('Failed to parse spreadsheet. Please ensure it\'s a valid Excel (.xlsx, .xls) or CSV file.');
        addLog(`File parsing failed - ${err.message}`, 'error');
      }
    };
    reader.onerror = (err) => {
      console.error('File reading error:', err);
      setError('Failed to read file');
      addLog(`File reading failed - ${err.message}`, 'error');
    };
    const isCSV = uploadedFile.name.toLowerCase().endsWith('.csv');
    if (isCSV) {
      reader.readAsText(uploadedFile);
    } else {
      reader.readAsBinaryString(uploadedFile);
    }
  };

  const handleMappingChange = (header, field) => {
    setMappings(prev => ({
      ...prev,
      [header]: field
    }));
  };

  const handleSalesMappingChange = (header, field) => {
    setSalesMappings(prev => ({
      ...prev,
      [header]: field
    }));
  };

  const handlePreview = async () => {
    // Verify required fields are mapped
    const mappedFields = Object.values(mappings);
    const missingRequiredFields = requiredFields
      .filter(field => !mappedFields.includes(field.key));

    if (missingRequiredFields.length > 0) {
      setError(`Missing required fields: ${missingRequiredFields.map(f => f.label).join(', ')}`);
      return;
    }

    // Clear previous state
    setError(null);
    // Keep existing logs but add a separator
    addLog('-------------------', 'info', 'import');
    setPendingSoldCoins([]);
    setSoldCoinTransactions({});
    addLog('Validating data...');

    try {
      let validatedData = [];
      const transactions = [];
      let errorCount = 0;

      addLog(
        `Starting validation of ${data.length} rows`,
        'info',
        'validation',
        'Checking required fields, data types, and field formats'
      );

      // Validate each row
      for (let i = 0; i < data.length; i++) {
        const row = data[i];
        try {
          // Log start of row validation
          addLog(
            `Validating row ${i + 1}`,
            'info',
            'validation',
            `Processing data from spreadsheet row ${i + 1}`
          );

          const result = validateRow(row, i, headers, mappings);
          
          if (!result.isValid) {
            // Group validation errors by type
            const fieldErrors = result.errors.filter(e => e.includes('field'));
            const formatErrors = result.errors.filter(e => e.includes('format'));
            const otherErrors = result.errors.filter(e => !e.includes('field') && !e.includes('format'));
            
            let errorDetails = '';
            if (fieldErrors.length) errorDetails += `Field Errors:\n${fieldErrors.join('\n')}\n`;
            if (formatErrors.length) errorDetails += `Format Errors:\n${formatErrors.join('\n')}\n`;
            if (otherErrors.length) errorDetails += `Other Errors:\n${otherErrors.join('\n')}`;
            
            addLog(
              `Row ${i + 1}: Validation failed`,
              'error',
              'validation',
              errorDetails.trim()
            );
            errorCount++;
            continue;
          }

          validatedData.push(result.data);
          
          if (result.isSold) {
            transactions.push({ rowIndex: i });
            addLog(
              `Row ${i + 1}: Detected sold coin`,
              'info',
              'validation',
              'Will prompt for transaction details after validation'
            );
          }

          if (result.pcgsUpdates) {
            setPcgsUpdates(prev => ({
              ...prev,
              [i]: result.pcgsUpdates
            }));
            
            // Log PCGS updates with before/after comparison
            const updates = Object.entries(result.pcgsUpdates.updates)
              .map(([field, newValue]) => {
                const oldValue = result.pcgsUpdates.original[field];
                return `${field}: "${oldValue}" → "${newValue}"`;
              })
              .join('\n');
            
            addLog(
              `Row ${i + 1}: PCGS data retrieved successfully`,
              'success',
              'pcgs-updates',
              `Field updates:\n${updates}`
            );
          }

          addLog(
            `Row ${i + 1}: Validation successful`,
            'success',
            'validation'
          );
        } catch (err) {
          addLog(`Row ${i + 1}: Validation failed - ${err.message}`, 'error');
          errorCount++;
        }
      }

      if (errorCount > 0) {
        setError(`Validation failed with ${errorCount} errors. Please check the logs.`);
        return;
      }

      // Collect certification numbers that need updates
      const certNumbers = [];
      validatedData.forEach((coin, index) => {
        if ((updateFromPCGS || fetchImages) && coin.certification_number && !revertedUpdates.has(index)) {
          certNumbers.push(coin.certification_number);
        }
      });

      // Fetch certification data if needed
      if (certNumbers.length > 0) {
        addLog(
          `Starting certification data lookup for ${certNumbers.length} coins`,
          'info',
          'cert-lookup'
        );

        const certData = await processCertBatch(certNumbers);

        // Update validated data with certification data
        validatedData = validatedData.map((coin, index) => {
          if (coin.certification_number && certData[coin.certification_number]) {
            const certInfo = certData[coin.certification_number];
            
            // Create a copy of original data for updates tracking
            setPcgsUpdates(prev => ({
              ...prev,
              [index]: {
                original: { ...coin },
                updates: certInfo
              }
            }));

            // Format images to match expected structure
            const formattedImages = certInfo.images?.map(img => ({
              image_url: img.url || img.image_url, // Handle both formats
              image_type: img.type || img.image_type // Handle both formats
            })).filter(img => img.image_url && img.image_type) || [];

            return {
              ...coin,
              ...certInfo,
              images: formattedImages,
              // Keep the certification service if lookup was successful
              certification_service_id: certInfo.certification_service_id
            };
          }
          return coin;
        });
      }

      // Handle transactions
      if (transactions.length > 0) {
        const soldCoins = transactions.map(t => ({
          rowIndex: t.rowIndex,
          data: validatedData[t.rowIndex]
        }));
        setPendingSoldCoins(soldCoins);
        addLog(`Found ${transactions.length} sold coin(s) that need transaction details`);
      }

      setValidatedTransactions(transactions);
      setPreviewData(validatedData);
      setShowPreview(true);
      addLog('Data validated successfully. Please review the preview before importing.');
    } catch (err) {
      setError('Validation failed. Please check the logs for details.');
      addLog('Validation failed: ' + err.message, 'error');
    }
  };

  const handleTransactionSubmit = (transactionData) => {
    setSoldCoinTransactions(prev => ({
      ...prev,
      [currentSoldCoin.rowIndex]: transactionData
    }));
    setShowTransactionModal(false);
    
    // Move to next coin that needs transaction data
    const nextCoin = pendingSoldCoins.find(coin => 
      !soldCoinTransactions[coin.rowIndex] && 
      coin.rowIndex !== currentSoldCoin.rowIndex
    );
    
    if (nextCoin) {
      setCurrentSoldCoin(nextCoin);
      setShowTransactionModal(true);
    }
  };

  const handleImport = async () => {
    if (!previewData) {
      setError('Please validate the data first');
      return;
    }

    setImporting(true);
    setError(null);
    // Keep existing logs but add a separator
    addLog('-------------------', 'info', 'import');
    addLog('Starting import...', 'info', 'import');

    try {
      let successCount = 0;
      let errorCount = 0;
      const createdCoins = [];

      // Create all coins
      for (let i = 0; i < previewData.length; i++) {
        try {
          let coinData = { ...previewData[i] };
          
          // For sold coins, initially set status to Inventory
          if (coinData.status?.toLowerCase() === 'sold') {
            coinData.status = 'Inventory';
          }

          // Generate user_coin_id if not provided
          if (!coinData.user_coin_id) {
            try {
              const response = await coins.getNextUserCoinId();
              coinData.user_coin_id = response.nextUserCoinId;
              addLog(`Row ${i + 1}: Generated User Coin ID: ${coinData.user_coin_id}`);
            } catch (err) {
              addLog(`Row ${i + 1}: Failed to generate User Coin ID - ${err.message}`, 'error');
            }
          }


          // Create coin and handle image saving
          const coin = await coins.create(coinData);

          // Add tags to coin after creation
          if (selectedTags.length > 0) {
            const tagIds = selectedTags.map(tag => tag.id);
            await tags.addTagsToCoin(coin.id, tagIds);
            addLog(
              `Row ${i + 1}: Added tags`,
              'success',
              'import',
              `Added tags: ${selectedTags.map(tag => tag.name).join(', ')}`
            );
          }

          // Images are already included in coinData and saved with coin.create()
          if (coinData.images?.length > 0) {
            addLog(
              `Row ${i + 1}: Certification images saved with coin data`,
              'success',
              'import'
            );
          }

          addLog(
            `Row ${i + 1}: Coin created successfully`,
            'success',
            'import',
            `Created coin with ID: ${coin.id}, User Coin ID: ${coinData.user_coin_id}`
          );
          createdCoins.push({ rowIndex: i, coin });
          successCount++;
        } catch (err) {
          const errorMessage = err.response?.data?.error || err.message;
          const errorDetails = err.response?.data?.details || err.response?.data;
          addLog(
            `Row ${i + 1}: Failed to create coin`,
            'error',
            'import',
            typeof errorDetails === 'object' 
              ? `Error: ${errorMessage}\nDetails: ${JSON.stringify(errorDetails, null, 2)}`
              : `Error: ${errorMessage}`
          );
          errorCount++;
        }
      }

      // Process transactions for sold coins
      for (const transaction of validatedTransactions) {
        const createdCoin = createdCoins.find(c => c.rowIndex === transaction.rowIndex);
        if (!createdCoin) continue;

        try {
          const transactionData = soldCoinTransactions[transaction.rowIndex];
          if (!transactionData) {
            throw new Error('Missing transaction data');
          }

          // Get the first (and only) price data entry
          const coinPriceData = Object.values(transactionData.coin_prices)[0];
          if (!coinPriceData) {
            throw new Error('Missing price data for coin');
          }

          // Create transaction with the expected format
          const transactionPayload = {
            coin_prices: {
              [createdCoin.coin.id]: {
                price: parseFloat(coinPriceData.price),
                fees: parseFloat(coinPriceData.fees || 0)
              }
            },
            client_id: parseInt(transactionData.client_id, 10),
            transaction_date: transactionData.transaction_date,
            notes: transactionData.notes || '',
            initialImport: true
          };

          await transactions.create(transactionPayload);
          addLog(
            `Row ${transaction.rowIndex + 1}: Created transaction successfully`,
            'success',
            'transactions',
            `Sale price: $${coinPriceData.price}, Fees: $${coinPriceData.fees || 0}, Client ID: ${transactionData.client_id}`
          );
        } catch (err) {
          const errorMessage = err.response?.data?.error || err.message;
          const errorDetails = err.response?.data?.details || err.response?.data;
          addLog(
            `Row ${transaction.rowIndex + 1}: Transaction failed`,
            'error',
            'transactions',
            typeof errorDetails === 'object'
              ? `Error: ${errorMessage}\nDetails: ${JSON.stringify(errorDetails, null, 2)}`
              : `Error: ${errorMessage}`
          );
          errorCount++;
        }
      }

      // Set final stats
      const stats = {
        total: successCount + errorCount,
        success: successCount,
        errors: errorCount,
        soldCoins: validatedTransactions.length
      };
      setImportStats(stats);
      addLog(`Import completed. ${successCount} coins imported, ${errorCount} errors`);
      
      if (successCount > 0) {
        setImportSuccess(true);
        // Scroll to success message
        setTimeout(() => {
          const element = document.getElementById('import-success-message');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message;
      const errorDetails = err.response?.data?.details || err.response?.data;
      setError('Import failed. Please check the logs for details.');
      addLog(
        'Import failed',
        'error',
        'import',
        typeof errorDetails === 'object'
          ? `Error: ${errorMessage}\nDetails: ${JSON.stringify(errorDetails, null, 2)}`
          : `Error: ${errorMessage}`
      );
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="bg-white shadow sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-between items-center">
            <div className={`flex-1 ${!headers.length ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
              1. Upload Spreadsheet
            </div>
            <div className={`flex-1 ${headers.length && !showPreview ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
              2. Map Columns
            </div>
            <div className={`flex-1 ${showPreview && !importSuccess ? 'text-indigo-600 font-medium' : 'text-gray-500'}`}>
              3. Review & Import
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">Step 1: Upload Your Spreadsheet</h3>
          
          <div className="mt-2 max-w-xl text-sm text-gray-500 space-y-2">
            <p>Upload a spreadsheet (.xlsx, .csv) file containing coin data. For sold coins, include transaction details and client information.</p>
            <p>
              <span className="font-medium">Using Google Sheets?</span> Go to File → Download → Microsoft Excel (.xlsx) and upload the file here.
            </p>
          </div>

          <div className="mt-5 space-y-4">
            {/* File Upload Section */}
            <div>
              <label htmlFor="coin-data-file" className="block text-sm font-medium text-gray-700">
                Coin Data Spreadsheet
              </label>
              <input
                id="coin-data-file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => handleFileUpload(e, false)}
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-indigo-50 file:text-indigo-700
                  hover:file:bg-indigo-100"
              />
            </div>
            
            {/* Sales Spreadsheet Section */}
            <div className="mt-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="useSalesSpreadsheet"
                  checked={useSalesSpreadsheet}
                  onChange={(e) => setUseSalesSpreadsheet(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="useSalesSpreadsheet" className="text-sm font-medium text-gray-700">
                  Use sales data spreadsheet
                </label>
              </div>
              
              {useSalesSpreadsheet && (
                <div className="mt-2">
                  <label htmlFor="sales-data-file" className="block text-sm font-medium text-gray-700">
                    Sales Data Spreadsheet
                  </label>
                  <input
                    id="sales-data-file"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => handleFileUpload(e, true)}
                    className="mt-1 block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-indigo-50 file:text-indigo-700
                      hover:file:bg-indigo-100"
                  />
                </div>
              )}
            </div>
            
            {/* Options Section */}
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="updateFromPCGS"
                  checked={updateFromPCGS}
                  onChange={(e) => setUpdateFromPCGS(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="updateFromPCGS" className="ml-2 block text-sm text-gray-900">
                  Update coin data from PCGS when certification number is available
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="fetchImages"
                  checked={fetchImages}
                  onChange={(e) => setFetchImages(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="fetchImages" className="ml-2 block text-sm text-gray-900">
                  Fetch certification images when available (PCGS/NGC)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Field Mapping Section */}
      {headers.length > 0 && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
              Step 2: Map Your Spreadsheet Columns
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Match your spreadsheet columns to the corresponding coin data fields. Required fields are marked with an asterisk (*).
            </p>

            <div className="space-y-8">
              {/* Coin Data Mappings */}
              <FieldMapping
                headers={headers}
                mappings={mappings}
                onMappingChange={handleMappingChange}
                availableFields={availableFields}
                title="Coin Data Mappings"
              />

              {/* Sales Data Mappings */}
              {useSalesSpreadsheet && salesHeaders.length > 0 && (
                <FieldMapping
                  headers={salesHeaders}
                  mappings={salesMappings}
                  onMappingChange={handleSalesMappingChange}
                  availableFields={salesFields}
                  title="Sales Data Mappings"
                />
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-5 space-x-3">
              {!showPreview ? (
                <button
                  type="button"
                  onClick={handlePreview}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Validate & Preview
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowPreview(false)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Back to Mapping
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const pendingManualTransactions = pendingSoldCoins.filter(coin => 
                        !soldCoinTransactions[coin.rowIndex]
                      );
                      
                      if (pendingManualTransactions.length > 0) {
                        const nextCoin = pendingManualTransactions[0];
                        setCurrentSoldCoin(nextCoin);
                        setShowTransactionModal(true);
                      } else {
                        handleImport();
                      }
                    }}
                    disabled={importing}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      importing
                        ? 'bg-indigo-400 cursor-not-allowed'
                        : pendingSoldCoins.length > 0 && Object.keys(soldCoinTransactions).length < pendingSoldCoins.length
                          ? 'bg-yellow-600 hover:bg-yellow-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                  >
                    {importing ? 'Importing...' : 
                      (() => {
                        const pendingManualTransactions = pendingSoldCoins.filter(coin => 
                          !soldCoinTransactions[coin.rowIndex]
                        );
                        return pendingManualTransactions.length > 0
                          ? `Enter Transaction Details (${Object.keys(soldCoinTransactions).length}/${pendingSoldCoins.length})`
                          : 'Confirm Import';
                      })()}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tag Selection Section */}
      {showPreview && previewData && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
              Add Tags (Optional)
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select or create tags that will be applied to all imported coins.
            </p>
            <TagSelector
              selectedTags={selectedTags}
              onChange={setSelectedTags}
              className="max-w-xl border border-gray-300 rounded-md"
            />
          </div>
        </div>
      )}

      {/* Data Preview Section */}
      {showPreview && previewData && (
        <DataPreview
          previewData={previewData}
          mappings={mappings}
          pcgsUpdates={pcgsUpdates}
          revertedUpdates={revertedUpdates}
          onRevertPcgsUpdates={(index) => {
            setRevertedUpdates(prev => new Set([...prev, index]));
            // Update preview data with original values
            const updatedPreviewData = [...previewData];
            const originalData = pcgsUpdates[index].original;
            const updatedFields = Object.keys(pcgsUpdates[index].updates);
            
            // Create new row data by starting with current data
            const newRowData = { ...updatedPreviewData[index] };
            
            // Revert only the fields that were updated by PCGS
            updatedFields.forEach(field => {
              newRowData[field] = originalData[field];
            });
            
            // Update the row
            updatedPreviewData[index] = newRowData;
            setPreviewData(updatedPreviewData);
            addLog(`Row ${index + 1}: Reverted PCGS updates`);
          }}
          updateFromPCGS={updateFromPCGS}
          availableFields={availableFields}
        />
      )}

      {/* Import Log Section */}
      {importSuccess ? (
        <div id="import-success-message" className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-green-900">Import Completed Successfully!</h3>
            <p className="mt-2 text-sm text-green-600">
              {importStats?.success} coins have been imported to your inventory.
            </p>
            <div className="mt-6">
              <button
                onClick={onComplete}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                View Imported Coins in Inventory →
              </button>
            </div>
          </div>
        </div>
      ) : (
        <ImportLog
          logs={logs}
          importStats={importStats}
          importSuccess={importSuccess}
          onComplete={onComplete}
        />
      )}

      {/* Sale Transaction Modal */}
      {showTransactionModal && currentSoldCoin && (
        <SaleTransactionModal
          isOpen={showTransactionModal}
          onClose={() => {
            setShowTransactionModal(false);
            setError('Transaction details are required for sold coins');
          }}
          onSubmit={handleTransactionSubmit}
          coins={[{
            id: null,
            ...currentSoldCoin.data,
            transactionNumber: Object.keys(soldCoinTransactions).length + 1,
            totalTransactions: pendingSoldCoins.length
          }]}
        />
      )}
    </div>
  );
};

export default BulkImportForm;
